import type { LucideIcon } from "lucide-react";
import { Reveal } from "@/components/Reveal";

export type FeatureItem = {
  title: string;
  description?: string;
  icon: LucideIcon;
  fullWidth?: boolean;
};

type FeatureGridProps = {
  items: FeatureItem[];
  columns?: 3 | 4 | 5;
  centered?: boolean;
};

export function FeatureGrid({ items, columns = 3, centered = false }: FeatureGridProps) {
  const gridCols =
    columns === 4
      ? "sm:grid-cols-2 lg:grid-cols-4"
      : columns === 5
        ? "sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
        : "sm:grid-cols-2 lg:grid-cols-3";

  return (
    <Reveal
      stagger
      className={`grid overflow-hidden rounded-3xl border border-white/10 grid-cols-1 feature-grid-${columns} ${gridCols}`}
    >
      {items.map((item) => {
        const Icon = item.icon;
        const isFullWidth = item.fullWidth;

        return (
          <div
            key={item.title}
            className={`feature-grid-item group relative bg-[#0b0b0b] p-8 transition hover:bg-white/[0.04] hover:z-10 ${
              isFullWidth
                ? "col-span-full !border-l-0 flex flex-col items-center text-center bg-black hover:bg-white/[0.02]"
                : centered
                  ? "flex flex-col items-center text-center"
                  : ""
            }`}
          >
            <Icon
              className={`mb-10 h-8 w-8 text-accent ${isFullWidth || centered ? "mx-auto" : ""}`}
            />

            <h3 className="display text-2xl uppercase text-white">{item.title}</h3>

            {item.description && (
              <p
                className={`mt-5 leading-relaxed text-white/60 ${
                  isFullWidth ? "max-w-3xl mx-auto" : ""
                }`}
              >
                {item.description}
              </p>
            )}

            <div
              className={`absolute bottom-0 h-px w-0 bg-accent transition-all duration-500 group-hover:w-full ${
                isFullWidth || centered ? "left-1/2 -translate-x-1/2" : "left-0"
              }`}
            />
          </div>
        );
      })}
    </Reveal>
  );
}
